import type {
  CommentId,
  MessageId,
  TaskId,
  CommentAnchor,
  Comment,
  UserId,
  AttachmentId,
  Attachment,
  MockCache,
  StorageFolderId,
  WorkspaceId,
} from '../../types';

import { isGeneralFolder } from '../../storage-folders/tree';
import { fanoutLast } from '../fanout';
import { addToIndex } from '../indexes';
import type { RichDoc } from '../../rich-doc';
import { richDocFrom, richDocToText } from '../../rich-doc';
import { refreshMentions, snapshot, taskKeys } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

import { removeAnnotationsOf } from './annotations';
import { removeTextureLinksOf } from './texture-links';
import { selectStorageSettings } from './storage';

export function addComment(args: {
  id: CommentId;
  taskId: TaskId;
  bodyJson: RichDoc;
  replyToId?: CommentId | null;
  anchor?: CommentAnchor | null;
}): Comment {
  const cache = requireCache();
  if (!cache.tasks.has(args.taskId)) {
    throw new Error('Задача не найдена');
  }
  const now = cache.nowMs();
  const bodyJson = richDocFrom(args.bodyJson);
  const comment: Comment = {
    id: args.id,
    taskId: args.taskId,
    authorId: cache.me,
    bodyJson,
    bodyText: richDocToText(bodyJson),
    replyToId: args.replyToId ?? null,
    anchor: args.anchor ?? null,
    assigneeId: null,
    resolvedAt: null,
    editedAt: null,
    deletedAt: null,
    createdAt: now,
  };
  cache.comments.set(comment.id, comment);
  addToIndex(cache, 'comment', String(comment.id));
  refreshMentions(cache, 'comment', String(comment.id), comment.bodyText);
  logActivity(cache, {
    mutator: 'comment.add',
    entityTable: 'comment',
    entityId: String(comment.id),
    op: 'insert',
    before: null,
    after: snapshot(comment),
    taskIds: [args.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, args.taskId), 'inbox');
  return comment;
}

export function editComment(args: { id: CommentId; bodyJson: RichDoc }): void {
  const cache = requireCache();
  const comment = cache.comments.get(args.id);
  if (comment === undefined) {
    throw new Error('Комментарий не найден');
  }
  const before = { bodyJson: comment.bodyJson, bodyText: comment.bodyText, editedAt: comment.editedAt };
  comment.bodyJson = richDocFrom(args.bodyJson);
  comment.bodyText = richDocToText(comment.bodyJson);
  comment.editedAt = cache.nowMs();
  refreshMentions(cache, 'comment', String(comment.id), comment.bodyText);
  logActivity(cache, {
    mutator: 'comment.edit',
    entityTable: 'comment',
    entityId: String(comment.id),
    op: 'update',
    before,
    after: { bodyText: comment.bodyText, editedAt: comment.editedAt },
    taskIds: [comment.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, comment.taskId));
}

export function resolveComment(args: { id: CommentId }): void {
  const cache = requireCache();
  const comment = cache.comments.get(args.id);
  if (comment === undefined) {
    throw new Error('Комментарий не найден');
  }
  const before = { resolvedAt: comment.resolvedAt };
  comment.resolvedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'comment.resolve',
    entityTable: 'comment',
    entityId: String(comment.id),
    op: 'update',
    before,
    after: { resolvedAt: comment.resolvedAt },
    taskIds: [comment.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, comment.taskId));
}

export function unresolveComment(args: { id: CommentId }): void {
  const cache = requireCache();
  const comment = cache.comments.get(args.id);
  if (comment === undefined) {
    throw new Error('Комментарий не найден');
  }
  const before = { resolvedAt: comment.resolvedAt };
  comment.resolvedAt = null;
  logActivity(cache, {
    mutator: 'comment.resolve',
    entityTable: 'comment',
    entityId: String(comment.id),
    op: 'update',
    before,
    after: { resolvedAt: null },
    taskIds: [comment.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, comment.taskId));
}

export function setCommentAssignee(args: { id: CommentId; assigneeId: UserId | null }): void {
  const cache = requireCache();
  const comment = cache.comments.get(args.id);
  if (comment === undefined) {
    throw new Error('Комментарий не найден');
  }
  const before = { assigneeId: comment.assigneeId };
  comment.assigneeId = args.assigneeId;
  logActivity(cache, {
    mutator: 'comment.setAssignee',
    entityTable: 'comment',
    entityId: String(comment.id),
    op: 'update',
    before,
    after: { assigneeId: comment.assigneeId },
    taskIds: [comment.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, comment.taskId), 'inbox');
}

export const DEFAULT_MAX_ATTACHMENT_BYTES = 200 * 1024 * 1024;

const MAX_ATTACHMENT_NAME_LENGTH = 255;
const MIME_PATTERN = /^[^/\s]+\/[^/\s]+$/;

export function maxAttachmentBytes(cache: MockCache): number {
  return selectStorageSettings(cache)?.maxAttachmentBytes ?? DEFAULT_MAX_ATTACHMENT_BYTES;
}

export const ATTACHMENTS_KEY = 'attachments';

function ownerKeys(
  cache: MockCache,
  owner: Pick<Attachment, 'ownerKind' | 'ownerId'>,
  draftTaskId: TaskId | undefined,
): string[] {
  if (owner.ownerKind === 'task') return taskKeys(cache, owner.ownerId as TaskId);
  if (owner.ownerKind === 'doc') return ['docs', `doc:${String(owner.ownerId)}`];
  if (owner.ownerKind === 'comment') {
    const taskId = cache.comments.get(owner.ownerId as CommentId)?.taskId ?? draftTaskId;
    return taskId === undefined ? [] : taskKeys(cache, taskId);
  }
  const message = cache.messages.get(owner.ownerId as MessageId);
  if (message === undefined) return [];
  const keys = [`channel:${String(message.channelId)}`];
  if (message.threadRootId !== null) keys.push(`thread:${String(message.threadRootId)}`);
  return keys;
}

export function attachmentOwnerKeys(
  cache: MockCache,
  owner: Pick<Attachment, 'ownerKind' | 'ownerId'>,
  draftTaskId: TaskId | undefined,
): string[] {
  return [...ownerKeys(cache, owner, draftTaskId), ATTACHMENTS_KEY];
}

export function requireAttachmentName(raw: string): string {
  const name = raw.trim();
  if (name.length === 0 || name.length > MAX_ATTACHMENT_NAME_LENGTH) {
    throw new Error('Неверное имя файла');
  }
  return name;
}

function assertAttachmentArgs(cache: MockCache, args: { name: string; mime: string; sizeBytes: number }): void {
  if (!Number.isInteger(args.sizeBytes) || args.sizeBytes < 0) {
    throw new Error('Неверный размер файла');
  }
  const limit = maxAttachmentBytes(cache);
  if (args.sizeBytes > limit) {
    throw new Error(`Файл больше ${Math.floor(limit / (1024 * 1024))} МБ`);
  }
  requireAttachmentName(args.name);
  if (!MIME_PATTERN.test(args.mime)) {
    throw new Error('Неверный тип файла');
  }
}

function assertStoragePlacement(
  cache: MockCache,
  args: Pick<Attachment, 'ownerKind' | 'ownerId'> & { folderId?: StorageFolderId | null },
): void {
  const folderId = args.folderId ?? null;
  if (args.ownerKind !== 'storage') {
    if (folderId !== null) {
      throw new Error('Вложение сообщения или задачи кладётся в папку переносом после загрузки');
    }
    return;
  }
  if (!cache.workspaces.has(args.ownerId as WorkspaceId)) {
    throw new Error('Рабочее пространство не найдено');
  }
  if (folderId !== null && !isGeneralFolder(cache, folderId)) {
    throw new Error('Папка не найдена');
  }
}

export function addAttachment(args: {
  id: AttachmentId;
  ownerKind: Attachment['ownerKind'];
  ownerId: Attachment['ownerId'];
  name: string;
  mime: string;
  sizeBytes: number;
  taskId?: TaskId;
  folderId?: StorageFolderId | null;
}): void {
  const cache = requireCache();
  assertAttachmentArgs(cache, args);
  assertStoragePlacement(cache, args);
  const now = cache.nowMs();
  const kind: Attachment['kind'] = args.mime.startsWith('image/')
    ? 'image'
    : args.mime.startsWith('video/')
      ? 'video'
      : args.mime.startsWith('audio/')
        ? 'audio'
        : 'file';
  cache.attachments.set(args.id, {
    id: args.id,
    ownerKind: args.ownerKind,
    ownerId: args.ownerId,
    name: args.name,
    mime: args.mime,
    sizeBytes: args.sizeBytes,
    url: '',
    width: null,
    height: null,
    kind,
    status: 'pending',
    uploaderId: cache.me,
    createdAt: now,
    folderId: args.folderId ?? null,
  });
  if (args.ownerKind !== 'storage') {
    logActivity(cache, {
      mutator: 'attachment.add',
      entityTable: 'attachment',
      entityId: String(args.id),
      op: 'insert',
      before: null,
      after: { name: args.name, sizeBytes: args.sizeBytes },
      taskIds: args.taskId === undefined ? [] : [args.taskId],
    });
    fanoutLast(cache);
  }
  bump(...attachmentOwnerKeys(cache, args, args.taskId));
}

export function markAttachmentReady(args: { id: AttachmentId }): void {
  const cache = requireCache();
  const attachment = cache.attachments.get(args.id);
  if (attachment === undefined || attachment.status === 'ready') return;
  attachment.status = 'ready';
  bump(...attachmentOwnerKeys(cache, attachment, undefined));
}

export function removeAttachmentExtras(cache: MockCache, attachmentId: AttachmentId): void {
  removeAnnotationsOf(cache, attachmentId);
  cache.attachmentThumbnails.delete(attachmentId);
  removeTextureLinksOf(cache, attachmentId);
}

export function removeAttachment(args: { id: AttachmentId; taskId?: TaskId }): void {
  const cache = requireCache();
  const attachment = cache.attachments.get(args.id);
  if (attachment === undefined) return;
  cache.attachments.delete(args.id);
  removeAttachmentExtras(cache, args.id);
  if (attachment.ownerKind !== 'storage') {
    logActivity(cache, {
      mutator: 'attachment.remove',
      entityTable: 'attachment',
      entityId: String(args.id),
      op: 'delete',
      before: { name: attachment.name },
      after: null,
      taskIds: args.taskId === undefined ? [] : [args.taskId],
    });
    fanoutLast(cache);
  }
  bump(...attachmentOwnerKeys(cache, attachment, args.taskId));
}

export function discardDraftAttachments(args: { ownerId: CommentId; taskId?: TaskId }): void {
  const cache = requireCache();
  if (cache.comments.has(args.ownerId)) return;
  let removed = 0;
  for (const attachment of [...cache.attachments.values()]) {
    if (attachment.ownerKind === 'comment' && String(attachment.ownerId) === String(args.ownerId)) {
      cache.attachments.delete(attachment.id);
      removeAttachmentExtras(cache, attachment.id);
      removed += 1;
    }
  }
  if (removed === 0) return;
  bump(ATTACHMENTS_KEY, ...(args.taskId === undefined ? [] : taskKeys(cache, args.taskId)));
}
